<template>
  <v-card v-if="alarm" color="error" dark class="elevation-4">
    <v-card-title class="text-h5 font-weight-bold white--text">
      <v-icon large left color="white">mdi-alert-octagram</v-icon>
      <span class="white--text">HÄLYTYS!</span>
    </v-card-title>
    <v-card-text class="white--text">
      <div class="text-h5 mb-2">
        {{ alarm.code }}
        - {{ alarmLabel }}
      </div>
      <div class="text-h6 mb-2">{{ alarm.address }}</div>
      <div class="subtitle-1">
        {{ formattedTime }}
      </div>

      <!-- Units Display -->
      <div
        v-if="ownUnits.length > 0"
        class="text-h4 font-weight-bold mt-2 mb-1"
      >
        {{ ownUnits.join(", ") }}
      </div>
      <div
        v-if="otherUnits.length > 0"
        class="text-body-2 grey--text text--lighten-2 mb-3"
      >
        Muut yksiköt: {{ otherUnits.join(", ") }}
      </div>

      <alarm-timer v-if="alarmDateTime" :start-time="alarmDateTime" />

      <div v-if="participantsList.length > 0" class="mt-3">
        <div class="text-subtitle-2 grey--text text--lighten-3 mb-1">
          Osallistujat ({{ participantsList.length }}):
        </div>
        <div class="d-flex flex-wrap">
          <v-chip
            v-for="(participant, i) in participantsList"
            :key="i"
            small
            label
            :color="participant.isYj ? 'white' : 'white'"
            :text-color="participant.isYj ? 'red darken-4' : 'black'"
            class="mr-1 mb-1"
          >
            <v-icon v-if="participant.smokediver && isSmokeDiverTask" left small
              >mdi-smoke</v-icon
            >
            {{ participant.name }}
          </v-chip>
        </div>
      </div>

      <div v-if="isVehicleRelatedTask" class="mt-4">
        <v-card
          outlined
          color="transparent"
          class="pa-2"
          style="border-color: rgba(255, 255, 255, 0.3) !important"
        >
          <div
            class="text-subtitle-2 font-weight-bold mb-1 white--text d-flex align-center justify-space-between flex-wrap"
          >
            <div class="d-flex align-center">
              <v-icon left small color="white">mdi-car-search</v-icon>
              Hae ajoneuvon tiedot / pelastuskortti
            </div>
            <v-btn
              text
              x-small
              dark
              class="white--text font-weight-bold"
              @click="showPlateHistoryDialog = true"
            >
              <v-icon left x-small>mdi-history</v-icon>
              Hakuhistoria
            </v-btn>
          </div>
          <v-form @submit.prevent="goToAjoneuvotiedot">
            <v-row dense align="center">
              <v-col cols="12" sm="8" class="d-flex align-center">
                <v-text-field
                  v-model="vehicleRegNumber"
                  placeholder="Rekisteritunnus (esim. ABC-123)"
                  solo
                  light
                  dense
                  hide-details
                  clearable
                  :disabled="ocrLoading"
                />
                <input
                  ref="alarmPlateCameraInput"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style="display: none"
                  @change="onPlateImageCaptured"
                />
                <v-btn
                  icon
                  color="white"
                  class="ml-1"
                  :loading="ocrLoading"
                  title="Ota kuva rekisterikilvestä kameralla"
                  @click="$refs.alarmPlateCameraInput.click()"
                >
                  <v-icon>mdi-camera</v-icon>
                </v-btn>
              </v-col>
              <v-col cols="12" sm="4">
                <v-btn
                  color="white"
                  light
                  block
                  :disabled="!vehicleRegNumber || ocrLoading"
                  type="submit"
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Hae tiedot
                </v-btn>
              </v-col>
            </v-row>
            <div
              v-if="ocrLoading"
              class="text-caption white--text mt-1 d-flex align-center"
            >
              <v-progress-circular
                indeterminate
                size="14"
                width="2"
                color="white"
                class="mr-2"
              />
              Tunnistetaan rekisterikilpeä kuvasta...
            </div>
          </v-form>
        </v-card>
      </div>

      <!-- Ensivaste / EVY pikanosto & kertaus -->
      <div v-if="isEvyTask" class="mt-4">
        <v-card
          outlined
          color="rgba(0, 0, 0, 0.25)"
          class="pa-3 rounded-xl"
          style="border-color: rgba(255, 255, 255, 0.4) !important"
        >
          <div class="d-flex align-center justify-space-between mb-2 flex-wrap">
            <div
              class="text-subtitle-1 font-weight-bold white--text d-flex align-center"
            >
              <v-icon left small color="amber accent-2">mdi-medical-bag</v-icon>
              Ensivasteen pikakertaus:
              {{ evyGuide ? evyGuide.title : alarm.code }}
            </div>
            <v-btn
              color="white"
              light
              small
              class="font-weight-bold my-1"
              @click="showEvyDialog = true"
            >
              <v-icon left small color="primary"
                >mdi-book-open-page-variant</v-icon
              >
              Kertaa ohje
            </v-btn>
          </div>

          <div
            v-if="evyGuide"
            class="white--text text-body-2 font-weight-medium"
          >
            {{ evyGuide.shortSummary }}
          </div>
        </v-card>
      </div>

      <div
        v-if="isMapSupported"
        class="mt-4 rounded overflow-hidden"
        style="height: 400px; position: relative; z-index: 1"
      >
        <l-map
          v-if="alarmCoords"
          :zoom="13"
          :center="alarmCoords"
          style="height: 100%; width: 100%"
        >
          <l-tile-layer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          ></l-tile-layer>
          <l-marker :lat-lng="alarmCoords"></l-marker>
          <l-marker :lat-lng="stationCoords"></l-marker>
          <l-polyline
            v-if="routeLatlngs.length > 0"
            :lat-lngs="routeLatlngs"
            color="blue"
            :weight="5"
          ></l-polyline>
        </l-map>
        <div
          v-else
          class="d-flex align-center justify-center fill-height grey lighten-3"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </div>
      </div>
    </v-card-text>
    <v-card-actions class="d-flex flex-wrap">
      <v-btn
        color="white"
        light
        class="flex-grow-1 ma-1"
        :to="{
          path: '/kartta',
          query: {
            lat: alarmCoords ? alarmCoords[0] : undefined,
            lng: alarmCoords ? alarmCoords[1] : undefined,
            zoom: 15,
          },
        }"
      >
        <v-icon left>mdi-map</v-icon>
        Avaa kartalla
      </v-btn>
      <v-btn
        v-if="alarmCoords"
        class="flex-grow-1 ma-1"
        text
        color="white"
        @click="openGoogleMaps"
      >
        <v-icon left>mdi-google-maps</v-icon>
        Google Maps
      </v-btn>
      <v-btn
        v-if="isEvyTask"
        class="flex-grow-1 ma-1 font-weight-bold"
        color="white"
        light
        @click="showEvyDialog = true"
      >
        <v-icon left color="primary">mdi-medical-bag</v-icon>
        EVY-kertaus
      </v-btn>
      <v-btn
        class="flex-grow-1 ma-1"
        text
        color="white"
        :to="`/digivihot/d/${alarm.id}`"
      >
        Avaa Digivihko
        <v-icon right>mdi-arrow-right</v-icon>
      </v-btn>
    </v-card-actions>
    <ensivaste-quick-guide
      v-if="isEvyTask"
      v-model="showEvyDialog"
      :code="alarm.code"
    />
    <plate-search-history-dialog
      v-model="showPlateHistoryDialog"
      @select="onSelectHistoryPlate"
    />
  </v-card>
</template>

<script>
import { LMap, LTileLayer, LMarker, LPolyline } from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import dayjs from "dayjs";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { firestore as db } from "@/plugins/firebase.app";
import tehtavaluokat from "@/assets/tehtavaluokat";
import AlarmTimer from "@/components/Ilmoitustaulu/AlarmTimer";
import EnsivasteQuickGuide from "@/components/Ensivaste/EnsivasteQuickGuide";
import PlateSearchHistoryDialog from "@/components/PlateSearchHistoryDialog";
import { getOhjeByCode, isEvyCode } from "@/assets/ensivasteOhjeet";
import { addPlateSearchHistory } from "@/utils/plateSearchHistory";
import api from "@/api";

const EXCLUDED_ADDRESSES = [
  "Parkanontie, Pori",
  "Vaasantie, Pori",
  "**********",
];

// Fix default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  name: "AlarmMap",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPolyline,
    AlarmTimer,
    EnsivasteQuickGuide,
    PlateSearchHistoryDialog,
  },
  data() {
    return {
      alarm: null,
      alarmCoords: null,
      stationCoords: [61.59178, 21.86803], // Palotie 4, Noormarkku
      routeLatlngs: [],
      ownUnits: [],
      otherUnits: [],
      intervalId: null,
      vehicleRegNumber: "",
      ocrLoading: false,
      showEvyDialog: false,
      showPlateHistoryDialog: false,
    };
  },
  computed: {
    formattedTime() {
      if (!this.alarm) return "";
      let dateTimeStr = this.alarm.date;
      if (this.alarm.time) {
        dateTimeStr += "T" + this.alarm.time;
        if (this.alarm.time.length === 5) dateTimeStr += ":00";
      }
      return dayjs(dateTimeStr).format("dd DD.MM.YYYY HH:mm:ss");
    },
    alarmDateTime() {
      if (!this.alarm) return null;
      let dateTimeStr = this.alarm.date;
      if (this.alarm.time) {
        dateTimeStr += "T" + this.alarm.time;
        if (this.alarm.time.length === 5) dateTimeStr += ":00";
      }
      return dateTimeStr;
    },
    alarmLabel() {
      if (!this.alarm || !this.alarm.code) return "";
      const code = this.alarm.code.replace(/[^0-9]/g, "");
      const match = tehtavaluokat.find((t) => t.code === code);
      return match ? match.label : "";
    },
    isMapSupported() {
      if (!this.alarm || !this.alarm.address) return false;
      return !EXCLUDED_ADDRESSES.includes(this.alarm.address);
    },
    participantsList() {
      if (!this.alarm || !this.alarm.participants) return [];
      const list = [...this.alarm.participants.filter((p) => p.name)];
      return list.sort((a, b) => {
        if (a.isYj && !b.isYj) return -1;
        if (!a.isYj && b.isYj) return 1;
        return 0;
      });
    },
    isSmokeDiverTask() {
      if (!this.alarm || !this.alarm.code) return false;
      const code = this.alarm.code.replace(/[^0-9]/g, "");
      // Starts with 40 or 45
      if (code.startsWith("40") || code.startsWith("45")) return true;
      // Is 414, 415, 416
      if (["414", "415", "416", "103"].includes(code)) return true;
      return false;
    },
    isVehicleRelatedTask() {
      if (!this.alarm || !this.alarm.code) return false;
      const code = this.alarm.code.replace(/[^0-9]/g, "");
      // Tieliikenneonnettomuudet (20x), raideliikenne (21x), maastoliikenne (27x), liikennevälinepalo (41x)
      if (
        code.startsWith("20") ||
        code.startsWith("21") ||
        code.startsWith("27") ||
        code.startsWith("41")
      ) {
        return true;
      }
      return false;
    },
    isEvyTask() {
      if (!this.alarm || !this.alarm.code) return false;
      return isEvyCode(this.alarm.code);
    },
    evyGuide() {
      if (!this.alarm || !this.alarm.code) return null;
      return getOhjeByCode(this.alarm.code);
    },
  },
  watch: {
    alarm: {
      handler(newVal) {
        if (newVal) {
          this.parseUnits(newVal);
          if (newVal.address) {
            this.geocodeAlarm(newVal);
          }
        }
      },
    },
  },
  methods: {
    async checkActiveAlarm() {
      try {
        const digivihotRef = collection(db, "digivihot");
        // Fetch 5 latest by date
        const q = query(
          digivihotRef,
          where("type", "==", "Hälytys"),
          where("deleted", "==", false),
          orderBy("date", "desc"),
          limit(5),
        );

        const querySnapshot = await getDocs(q);
        if (querySnapshot.empty) {
          this.alarm = null;
          return;
        }

        const alarms = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort by date + time descending
        alarms.sort((a, b) => {
          let dateA = a.date;
          if (a.time) {
            dateA += "T" + a.time;
            if (a.time.length === 5) dateA += ":00";
          }
          let dateB = b.date;
          if (b.time) {
            dateB += "T" + b.time;
            if (b.time.length === 5) dateB += ":00";
          }
          return dayjs(dateB).diff(dayjs(dateA));
        });

        const latest = alarms[0];

        // Check time < 30 mins
        let dateTimeStr = latest.date;
        if (latest.time) {
          dateTimeStr += "T" + latest.time;
          if (latest.time.length === 5) dateTimeStr += ":00";
        }

        const alarmTime = dayjs(dateTimeStr);
        const now = dayjs();
        const diffMins = now.diff(alarmTime, "minute");

        // Näytetään aktiivisena 2 tuntia (120 min) hälytysajasta
        if (diffMins < 120) {
          // Only update if ID changed to avoid re-geocoding/re-rendering loops if not needed
          if (!this.alarm || this.alarm.id !== latest.id) {
            this.alarm = latest;
          }
        } else {
          this.alarm = null;
        }
      } catch (e) {
        console.error("Failed to check active alarm", e);
      }
    },
    parseUnits(alarm) {
      const description = alarm.description || "";
      const unitRegex = /\b[A-Z]{2,5}\d{2,4}\b/g;
      const allUnits = description.match(unitRegex) || [];

      // Unique units
      const uniqueUnits = [...new Set(allUnits)];

      this.ownUnits = uniqueUnits.filter((u) => u.startsWith("RSA51"));
      this.otherUnits = uniqueUnits.filter((u) => !u.startsWith("RSA51"));
    },
    async geocodeAlarm(alarm) {
      if (!alarm.address) return;

      if (!this.isMapSupported) {
        this.alarmCoords = null;
        this.routeLatlngs = [];
        return;
      }

      this.alarmCoords = null;
      this.routeLatlngs = [];
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            alarm.address,
          )}`,
        );
        const data = await response.json();
        if (data && data.length > 0) {
          this.alarmCoords = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
          this.fetchRoute();
        }
      } catch (e) {
        console.error("Geocoding failed", e);
      }
    },
    async fetchRoute() {
      if (!this.alarmCoords || !this.stationCoords) return;

      try {
        // OSRM uses lng,lat
        const start = `${this.stationCoords[1]},${this.stationCoords[0]}`;
        const end = `${this.alarmCoords[1]},${this.alarmCoords[0]}`;

        const response = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${start};${end}?overview=full&geometries=geojson`,
        );
        const data = await response.json();

        if (data.routes && data.routes.length > 0) {
          // Convert GeoJSON [lng, lat] to Leaflet [lat, lng]
          this.routeLatlngs = data.routes[0].geometry.coordinates.map(
            (coord) => [coord[1], coord[0]],
          );
        }
      } catch (e) {
        console.error("Route fetching failed", e);
      }
    },
    openGoogleMaps() {
      if (!this.alarmCoords) return;
      const [lat, lng] = this.alarmCoords;
      const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      window.open(url, "_blank");
    },
    onSelectHistoryPlate(reg) {
      this.vehicleRegNumber = reg;
      this.goToAjoneuvotiedot(reg);
    },
    goToAjoneuvotiedot(plateOverride) {
      const reg = (plateOverride || this.vehicleRegNumber || "")
        .trim()
        .toUpperCase();
      if (!reg) return;
      addPlateSearchHistory(reg);
      this.$router.push({
        path: "/ajoneuvotiedot",
        query: { rekisterinumero: reg },
      });
    },
    async onPlateImageCaptured(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;

      this.ocrLoading = true;
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = reader.result;
            const b64 = String(dataUrl).split(",")[1];
            resolve(b64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const mimeType = file.type || "image/jpeg";
        const result = (
          await api.recognizePlateNumber({
            imageBase64: base64,
            mimeType,
          })
        ).data;

        if (result.success && result.plateNumber) {
          this.vehicleRegNumber = result.plateNumber;
          this.$toast?.success?.(`Kilpi tunnistettu: ${result.plateNumber}`);
          this.goToAjoneuvotiedot();
        } else {
          this.$toast?.warning?.(
            result.error || "Rekisterikilpeä ei pystytty tunnistamaan kuvasta.",
          );
        }
      } catch (e) {
        this.$toast?.error?.(
          e.message || "Rekisterikilven kuvan käsittely epäonnistui",
        );
      } finally {
        this.ocrLoading = false;
        if (event?.target) event.target.value = "";
      }
    },
  },
  mounted() {
    this.checkActiveAlarm();
    this.intervalId = setInterval(this.checkActiveAlarm, 60000);
  },
  beforeDestroy() {
    if (this.intervalId) clearInterval(this.intervalId);
  },
};
</script>
